/**
 * `IFFIELD` — SHOW A REGION ONLY WHEN A YES/NO FIELD SAYS YES (2026-10-29, Layne + Jack).
 *
 * Layne's spec for the Next Gen logo on the Featured cards (ECH_Featured1CTA and
 * ECH_Featured2CTA): "a field ShowNextGen (yes/no) and a marker pair around the logo
 * row, like <!--IFFIELD:ShowNextGen--> … <!--/IFFIELD:ShowNextGen-->".
 *
 * It is the sibling of `IFCOLUMN` (echContactTreatment.ts), with one difference that
 * matters: IFCOLUMN keeps a region when its column has ANY value, and a yes/no field
 * always has a value ("No" is a value). So IFFIELD keeps the region only when the
 * field reads as yes — Yes / True / On / Show / 1 / Y, any case — and deletes it for
 * No, an empty field, or a field the block does not declare at all. The markers
 * themselves never reach the email.
 *
 * Generic on purpose: any block can wrap any region in IFFIELD:<its own field>, so
 * the next yes/no Layne adds needs no app change.
 */

/** what counts as "yes" for an IFFIELD region and for the fill that writes one */
export const ECH_IFFIELD_ON = /^(yes|y|true|on|show|1)$/i;
/** what counts as "no" — used only to find the block's own spelling of it */
export const ECH_IFFIELD_OFF = /^(no|n|false|off|hide|0)$/i;

const IFFIELD_ANY_RE = /<!--\s*IFFIELD:/i;
const IFFIELD_RE = /<!--\s*IFFIELD:([A-Za-z0-9_]+)\s*-->([\s\S]*?)<!--\s*\/IFFIELD:\1\s*-->/gi;

/** true when the block's markup carries at least one IFFIELD marker */
export function hasIfField(html: string): boolean {
  return IFFIELD_ANY_RE.test(String(html || ""));
}

/**
 * Keep each IFFIELD region whose field reads as yes (markers dropped), delete the rest.
 * Runs a few passes so a region nested inside another one is decided too.
 */
export function ifFieldHtml(html: string, d: Record<string, unknown>): string {
  let out = String(html || "");
  if (!hasIfField(out)) return out;
  for (let pass = 0; pass < 4; pass++) {
    const next = out.replace(IFFIELD_RE, (_w: string, k: string, body: string) =>
      ECH_IFFIELD_ON.test(String((d || {})[k] ?? "").trim()) ? body : "");
    if (next === out) break;
    out = next;
  }
  return out;
}
